import {
  Stack,
  Typography,
  Divider,
  Paper,
  Alert,
  Box,
  Grid,
  Link,
  Tooltip,
  IconButton,
  Button,
} from "@mui/material";
import { FindInPage as FindInPageIcon } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";

import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import cn from "classnames";
import { parse } from "tldts";

import { useQuery } from "@tanstack/react-query";
import styles from "./styles.module.css";

import Loading from "../Loading";
import theme from "../../theme";
import { useTrueBizApi } from "../../api";
import Gauge from "../Gauge";

export function FraudSearchResultBody({ searchId }: { searchId: UUID }) {
  const navigate = useNavigate();
  const api = useTrueBizApi();

  const [done, setDone] = useState(false);

  const query = useQuery({
    queryKey: ["getFraudSearch", searchId],
    queryFn: () => {
      if (!searchId) return;

      return api.getFraudSearch({ searchId });
    },
    enabled: searchId != null,
    refetchInterval: done ? undefined : 750,
  });

  const fraudSearch = query.data;

  useEffect(() => {
    if (!query.data?.status) return;

    if (query.data.status !== "PROCESSING") {
      setDone(true);
    }
  }, [query.data?.status]);

  if (!fraudSearch) {
    // this should not happen because we already warmed the cache
    throw new Error("Cache miss");
  }

  const searchParamPairs = useMemo(() => {
    if (!fraudSearch.query) return [];

    return [
      ["Business name", fraudSearch.query.business_name || "—"],
      ["Website", fraudSearch.query.website || "—"],
      ["City", fraudSearch.query.city || "—"],
      ["State/Province", fraudSearch.query.state_province || "—"],
      ["Postal code", fraudSearch.query.postal_code || "—"],
      ["Country", fraudSearch.query.country || "—"],
    ].filter(([k, v]) => v !== "—");
  }, [fraudSearch]);

  return (
    <Stack>
      <Stack direction="row" justifyContent="space-between" mb={1}>
        <Typography variant="h5">Propensity to Fraud</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            display="block"
            onClick={() => {
              navigate("/propensity-to-fraud/search");
            }}
          >
            New search
          </Button>
        </Box>
      </Stack>

      <Divider />

      <Link
        component={RouterLink}
        to={`/propensity-to-fraud/`}
        title="Back to Search History"
      >
        <div
          style={{
            display: "flex",
            paddingBottom: "0.5em",
            paddingTop: "0.5em",
            fontSize: "85%",
          }}
        >
          <ArrowBackIcon fontSize="small" /> Back to Search History
        </div>
      </Link>

      <Box marginBottom={3} marginTop={1}>
        {fraudSearch.status === "PROCESSING" && (
          <Alert severity="info">
            We&rsquo;re still analyzing all of the data for this search. This
            page will refresh when your search completes. Please check back in a
            few minutes.
          </Alert>
        )}
      </Box>

      <Box>
        <Grid container columnSpacing={3}>
          <Grid item xs={12}>
            <AnimatePresence>
              {fraudSearch.status === "PROCESSING" && (
                <motion.div
                  key="loading-animation"
                  exit={{
                    height: 0,
                    opacity: 0,
                  }}
                >
                  <Paper
                    sx={{
                      marginBottom: 3,
                      padding: 2,
                      height: "250px",
                    }}
                  >
                    <Loading>
                      {fraudSearch.status_description && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          key="status-description"
                          className={cn(styles.shimmering)}
                        >
                          {fraudSearch.status_description}
                        </motion.div>
                      )}
                    </Loading>
                  </Paper>
                </motion.div>
              )}
              {fraudSearch.status === "ERROR" && (
                <motion.div key="error-message">
                  <Typography color="error">
                    There was a problem executing your search. Please try again
                    shortly.
                  </Typography>
                </motion.div>
              )}
              {fraudSearch.status === "FINISHED" && (
                <motion.div key="result">
                  {fraudSearch.result && (
                    <Stack gap="1em">
                      <Paper sx={{ padding: 2 }}>
                        <Typography variant="h4">
                          {fraudSearch.result?.business_name ||
                            "Unknown business"}
                        </Typography>
                        {(fraudSearch.result?.website ||
                          fraudSearch.result?.business_address) && (
                          <Stack
                            direction="column"
                            gap="0.25em"
                            sx={{ color: theme.palette.grey[600] }}
                          >
                            {fraudSearch.result?.business_address
                              ?.full_address && (
                              <Typography>
                                {
                                  fraudSearch.result.business_address
                                    .full_address
                                }
                              </Typography>
                            )}
                            {fraudSearch.result?.website && (
                              <Stack direction="row">
                                <a
                                  href={fraudSearch.result.website}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {fraudSearch.result.website}
                                </a>
                                <div
                                  style={{
                                    height: "1.5em",
                                    lineHeight: 1,
                                    overflow: "visible",
                                    marginTop: "-0.3em",
                                  }}
                                >
                                  <Link
                                    href={`${window.location.origin}/domain/${
                                      parse(fraudSearch.result.website).domain
                                    }?run=1`}
                                    target="_blank"
                                  >
                                    <Tooltip
                                      title={
                                        "Run full web presence search on this domain in a new tab."
                                      }
                                      placement="right"
                                      arrow
                                    >
                                      <IconButton>
                                        <FindInPageIcon
                                          color="primary"
                                          fontSize="small"
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </Link>
                                </div>
                              </Stack>
                            )}
                          </Stack>
                        )}

                        {fraudSearch.result?.business_description && (
                          <Typography
                            sx={{
                              marginTop: 2,
                              marginBottom: 1,
                              fontSize: "95%",
                            }}
                          >
                            {fraudSearch.result.business_description}
                          </Typography>
                        )}
                      </Paper>
                      <Paper>
                        <Stack
                          sx={{
                            // border: `1px solid ${theme.palette.success.light}`,
                            borderRadius: "0.5em",
                            padding: "1.25em",
                            marginTop: 3,
                            marginBottom: 3,
                          }}
                        >
                          <Grid container columnSpacing={4}>
                            <Grid item xs={12}>
                              {fraudSearch.result?.fraud_likelihood_risk && (
                                <Gauge
                                  severity={
                                    fraudSearch.result.fraud_likelihood_risk ===
                                    "HIGH"
                                      ? "poor"
                                      : fraudSearch.result
                                          .fraud_likelihood_risk === "MEDIUM"
                                      ? "fair"
                                      : fraudSearch.result
                                          .fraud_likelihood_risk === "LOW"
                                      ? "good"
                                      : "fair"
                                  }
                                  ordering="worst-last"
                                  label={
                                    fraudSearch.result.fraud_likelihood_risk
                                  }
                                />
                              )}
                              <Typography
                                sx={{ textAlign: "center" }}
                                variant="subtitle2"
                              >
                                Likelihood of fraudulent behavior in the next
                                6&nbsp;months
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Stack
                                sx={{
                                  height: "100%",
                                  justifyContent: "center",
                                  paddingTop: "1em",
                                  textAlign: "center",
                                }}
                              >
                                {fraudSearch.result?.fraud_likelihood_summary}
                              </Stack>
                            </Grid>
                          </Grid>
                        </Stack>
                      </Paper>
                    </Stack>
                  )}
                  {!fraudSearch.result && (
                    <Typography>
                      We couldn&rsquo; find any results for your search.
                    </Typography>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </Grid>

          {/* SEARCH PARAMS */}
          <Grid item xs={5} mt={2}>
            <Grid container columnSpacing={3}>
              <Grid item xs={12} mb={2}>
                <div
                  style={{
                    border: "1px solid #ccc",
                    padding: "0.5em",
                    borderRadius: "5px",
                    background: "#fdfdfd",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      minHeight: "38px",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong style={{ color: "#555" }}>
                        Parameters for Search
                      </strong>
                    </div>
                  </div>
                  <hr
                    style={{
                      borderStyle: "solid",
                      borderColor: "#eee",
                    }}
                  />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gridRowGap: "0.5em",
                    }}
                  >
                    {searchParamPairs.map(([k, v]) => (
                      <div key={k}>
                        <span style={{ color: "#777" }}>{k}</span>
                        <br />
                        <span
                          style={{
                            wordBreak: "break-word",
                          }}
                        >
                          <strong style={{ color: "#555" }}>{v}</strong>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
}

export default function FraudSearchResult() {
  const api = useTrueBizApi();
  const params = useParams();
  const { searchId } = params;

  const query = useQuery({
    queryKey: ["getFraudSearch", searchId],
    queryFn: () => {
      if (!searchId) return;

      return api.getFraudSearch({ searchId });
    },
    enabled: searchId != null,
    retry: false,
  });

  if (!searchId)
    throw new Error("No searchId in route, this should not happen.");

  if (query.data) {
    return <FraudSearchResultBody searchId={searchId} />;
  }

  if (query.isError || query.isLoadingError) {
    if (query.error instanceof Response && query.error.status === 404) {
      return (
        <Typography variant="h6" textAlign="center">
          We couldn&rsquo;t find the search you were looking for.
        </Typography>
      );
    }

    return <Typography variant="h6">There was an error.</Typography>;
  }

  if (query.isLoading) {
    return <Loading />;
  }

  return null;
}
